export interface SavedPrinter {
  type: 'usb' | 'bluetooth';
  id: string; // vendorId_productId for USB, device.id for Bluetooth
  name: string;
}

export interface ReceiptItem {
  name: string;
  quantity: number;
  price: number;
}

export interface ReceiptData {
  orderNumber?: string;
  queueNumber?: string;
  items: ReceiptItem[];
  total: number;
  paymentMethod: string;
  amountReceived: number;
  change: number;
  customerName?: string;
  date?: Date;
  shouldCut?: boolean;
}

export interface PrinterSettings {
  enabled: boolean;
  autoCut: boolean;
}

// Helper to concatenate Uint8Arrays without spreading (fixes ES5 spread issues)
export const concatUint8Arrays = (arrays: Uint8Array[]): Uint8Array => {
  const totalLength = arrays.reduce((acc, arr) => acc + arr.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.length;
  }
  return result;
};

export const getSavedPrinter = (): SavedPrinter | null => {
  const saved = localStorage.getItem('selected_printer');
  return saved ? (JSON.parse(saved) as SavedPrinter) : null;
};

export const savePrinter = (printer: SavedPrinter): void => {
  localStorage.setItem('selected_printer', JSON.stringify(printer));
};

export const getPrinterSettings = (): PrinterSettings => {
  const saved = localStorage.getItem('printer_settings');
  return saved
    ? (JSON.parse(saved) as PrinterSettings)
    : { enabled: true, autoCut: true };
};

export const savePrinterSettings = (settings: PrinterSettings): void => {
  localStorage.setItem('printer_settings', JSON.stringify(settings));
};

export const getAuthorizedBluetoothDevice = async (
  id: string,
): Promise<BluetoothDevice | null> => {
  if (navigator.bluetooth?.getDevices) {
    const devices = await navigator.bluetooth.getDevices();
    return devices.find((d) => d.id === id) || null;
  }
  return null;
};

export const requestBluetoothDevice =
  async (): Promise<BluetoothDevice | null> => {
    if (!navigator.bluetooth) {
      throw new Error('Web Bluetooth is not supported in this browser.');
    }

    const services = [
      '000018f0-0000-1000-8000-00805f9b34fb', // Generic printer service
      '0000ff00-0000-1000-8000-00805f9b34fb', // Common printer service
      '49535343-fe7d-4ae5-8fa9-9fafd205e455', // ISSC service
      'e7e11001-4958-4975-a91e-f463751d0200', // Alternative generic printer
    ];

    try {
      // Try with filters first (often more reliable on mobile)
      // Some Android versions prefer namePrefix: '' over acceptAllDevices
      const device = await navigator.bluetooth.requestDevice({
        filters: [
          { namePrefix: '' },
          ...services.map((s) => ({ services: [s] })),
        ],
        optionalServices: services,
      });
      return device;
    } catch (err) {
      // If filters fail or user cancelled, try acceptAllDevices as fallback if it wasn't a cancel
      if ((err as Error).name === 'NotFoundError') throw err;

      console.warn(
        'Bluetooth filtered request failed, trying acceptAllDevices...',
        err,
      );
      return await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: services,
      });
    }
  };

export const requestUsbDevice = async (): Promise<USBDevice | null> => {
  if (!navigator.usb) {
    throw new Error('WebUSB is not supported in this browser.');
  }

  const device = await navigator.usb.requestDevice({ filters: [] });
  return device;
};

export const printRaw = async (
  data: Uint8Array,
  printer: SavedPrinter,
  activeDevice?: USBDevice | BluetoothDevice | null,
): Promise<USBDevice | BluetoothDevice | null> => {
  let device = activeDevice;

  const attemptPrint = async (targetDevice: USBDevice | BluetoothDevice) => {
    if (printer.type === 'usb') {
      const usbDevice = targetDevice as USBDevice;
      if (!usbDevice.opened) {
        await usbDevice.open();
      }
      await usbDevice.selectConfiguration(1);
      await usbDevice.claimInterface(0);
      await usbDevice.transferOut(1, data as unknown as BufferSource);
    } else {
      const btDevice = targetDevice as BluetoothDevice;
      // Helper for GATT timeout
      const withTimeout = <T>(
        promise: Promise<T>,
        timeoutMs: number,
      ): Promise<T> => {
        return Promise.race([
          promise,
          new Promise<T>((_, reject) =>
            setTimeout(
              () => reject(new Error('Printer operation timed out.')),
              timeoutMs,
            ),
          ),
        ]);
      };

      let server = btDevice.gatt?.connected ? btDevice.gatt : null;
      if (!server && btDevice.gatt) {
        try {
          server = await withTimeout(btDevice.gatt.connect(), 10000); // 10s timeout for connect
        } catch (err) {
          console.error('Bluetooth connection failed:', err);
          throw new Error(
            'Could not connect to Bluetooth printer. Ensure it is on and nearby.',
            { cause: err },
          );
        }
      }

      if (!server || !server.connected) {
        throw new Error('Failed to connect to Bluetooth printer.');
      }

      // Add a small delay to allow services to be ready
      await new Promise((resolve) => setTimeout(resolve, 500));

      const services = await withTimeout(server.getPrimaryServices(), 5000);
      let characteristic: BluetoothRemoteGATTCharacteristic | null = null;

      for (const service of services) {
        const characteristics = await withTimeout(
          service.getCharacteristics(),
          5000,
        );
        for (const c of characteristics) {
          if (c.properties.write || c.properties.writeWithoutResponse) {
            characteristic = c;
            break;
          }
        }
        if (characteristic) break;
      }

      if (!characteristic) {
        throw new Error(
          'No writable characteristic found on Bluetooth device.',
        );
      }

      await withTimeout(
        characteristic.writeValue(data as unknown as BufferSource),
        10000,
      ); // 10s timeout for write
    }
  };

  try {
    // 1. Try with provided/cached device
    if (printer.type === 'usb') {
      const [vendorId, productId] = printer.id.split('_').map(Number);
      const usbDevice = device as USBDevice | null;
      if (
        !usbDevice ||
        usbDevice.vendorId !== vendorId ||
        usbDevice.productId !== productId
      ) {
        const devices = (await navigator.usb?.getDevices()) || [];
        device =
          devices.find(
            (d) => d.vendorId === vendorId && d.productId === productId,
          ) || null;
      }
    } else {
      const btDevice = device as BluetoothDevice | null;
      if (!btDevice || btDevice.id !== printer.id) {
        device = await getAuthorizedBluetoothDevice(printer.id);
      }
    }

    if (!device) throw new Error('Device not authorized');

    await attemptPrint(device);
    return device; // Return the working device for caching
  } catch (err) {
    // 2. If failure is due to authorization/not found, trigger picker
    const errorMessage = (err as Error).message.toLowerCase();
    const isAuthError =
      errorMessage.includes('not authorized') ||
      errorMessage.includes('not found') ||
      errorMessage.includes('device not authorized') ||
      errorMessage.includes('user gesture');

    if (isAuthError) {
      console.log('Attempting automatic re-authorization...');
      if (printer.type === 'bluetooth') {
        device = await requestBluetoothDevice();
      } else {
        device = await requestUsbDevice();
      }

      if (device) {
        await attemptPrint(device);
        return device;
      }
    }

    // Handle GATT errors with a single automatic retry and disconnect
    if (
      errorMessage.includes('gatt') ||
      errorMessage.includes('operation failed')
    ) {
      console.warn('GATT error detected, attempting one-time retry...');
      const btDevice = device as BluetoothDevice | null;
      if (printer.type === 'bluetooth' && btDevice?.gatt?.connected) {
        try {
          btDevice.gatt.disconnect();
        } catch (e) {
          console.error('Error during GATT disconnect:', e);
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await attemptPrint(btDevice);
        return btDevice;
      }
    }

    throw err;
  }
};

export interface BatchPrintOptions {
  batchSize?: number; // Size of each batch in bytes (default: 1024)
  delayBetweenBatches?: number; // Delay between batches in milliseconds (default: 50)
}

/**
 * Splits large print data into batches and prints them sequentially with delays.
 * This prevents printer buffer overflow when printing receipts with many items.
 * For Bluetooth devices, maintains a single connection across all batches for efficiency.
 *
 * @param data - The complete print data to send
 * @param printer - The printer configuration
 * @param activeDevice - The active printer device (optional, will be reused across batches)
 * @param options - Batch printing options (batchSize, delayBetweenBatches)
 * @returns The active device after printing (for caching)
 */
export const printRawBatched = async (
  data: Uint8Array,
  printer: SavedPrinter,
  activeDevice?: USBDevice | BluetoothDevice | null,
  options: BatchPrintOptions = {},
): Promise<USBDevice | BluetoothDevice | null> => {
  const {
    batchSize: userBatchSize,
    delayBetweenBatches = 50, // Default 50ms delay between batches
  } = options;

  // Bluetooth characteristics have a 512-byte write limit
  // Use smaller batch size for Bluetooth, larger for USB
  const batchSize =
    userBatchSize ?? (printer.type === 'bluetooth' ? 512 : 1024); // 512 bytes for BT, 1KB for USB

  // If data is small enough, print directly without batching
  if (data.length <= batchSize) {
    return await printRaw(data, printer, activeDevice);
  }

  // Split data into batches
  const batches: Uint8Array[] = [];
  for (let i = 0; i < data.length; i += batchSize) {
    const end = Math.min(i + batchSize, data.length);
    batches.push(data.slice(i, end));
  }

  // For Bluetooth devices, maintain connection across batches
  if (printer.type === 'bluetooth') {
    return await printRawBatchedBluetooth(
      batches,
      printer,
      activeDevice as BluetoothDevice | null,
      delayBetweenBatches,
    );
  }

  // For USB devices, use standard batching (USB connections are more stable)
  let currentDevice: USBDevice | null = (activeDevice as USBDevice) || null;
  for (let i = 0; i < batches.length; i++) {
    const batch = batches[i];
    const isLastBatch = i === batches.length - 1;

    try {
      currentDevice = (await printRaw(
        batch,
        printer,
        currentDevice,
      )) as USBDevice | null;

      if (!isLastBatch && delayBetweenBatches > 0) {
        await new Promise((resolve) =>
          setTimeout(resolve, delayBetweenBatches),
        );
      }
    } catch (error) {
      throw new Error(
        `Failed to print batch ${i + 1} of ${batches.length}: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        { cause: error },
      );
    }
  }

  return currentDevice;
};

/**
 * Helper function to handle batched printing for Bluetooth devices.
 * Maintains a single GATT connection and characteristic across all batches.
 */
const printRawBatchedBluetooth = async (
  batches: Uint8Array[],
  printer: SavedPrinter,
  activeDevice: BluetoothDevice | null,
  delayBetweenBatches: number,
): Promise<BluetoothDevice | null> => {
  let device = activeDevice;

  // Helper for GATT timeout
  const withTimeout = <T>(
    promise: Promise<T>,
    timeoutMs: number,
  ): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(
          () => reject(new Error('Printer operation timed out.')),
          timeoutMs,
        ),
      ),
    ]);
  };

  // Get or establish device connection
  if (!device || device.id !== printer.id) {
    device = await getAuthorizedBluetoothDevice(printer.id);
  }

  if (!device) {
    throw new Error('Bluetooth device not authorized');
  }

  // Connect to GATT server
  let server = device.gatt?.connected ? device.gatt : null;
  if (!server && device.gatt) {
    try {
      server = await withTimeout(device.gatt.connect(), 10000);
    } catch (err) {
      console.error('Bluetooth connection failed:', err);
      throw new Error(
        'Could not connect to Bluetooth printer. Ensure it is on and nearby.',
        { cause: err },
      );
    }
  }

  if (!server || !server.connected) {
    throw new Error('Failed to connect to Bluetooth printer.');
  }

  // Get services and characteristic once
  await new Promise((resolve) => setTimeout(resolve, 500)); // Allow services to be ready

  const services = await withTimeout(server.getPrimaryServices(), 5000);
  let characteristic: BluetoothRemoteGATTCharacteristic | null = null;

  for (const service of services) {
    const characteristics = await withTimeout(
      service.getCharacteristics(),
      5000,
    );
    for (const c of characteristics) {
      if (c.properties.write || c.properties.writeWithoutResponse) {
        characteristic = c;
        break;
      }
    }
    if (characteristic) break;
  }

  if (!characteristic) {
    throw new Error('No writable characteristic found on Bluetooth device.');
  }

  // Print all batches using the same characteristic
  for (let i = 0; i < batches.length; i++) {
    const batch = batches[i];
    const isLastBatch = i === batches.length - 1;

    try {
      // Verify connection is still active before each batch
      if (!server.connected) {
        // Attempt to reconnect
        try {
          server = await withTimeout(device.gatt!.connect(), 10000);
          // Re-get characteristic after reconnection
          const services = await withTimeout(server.getPrimaryServices(), 5000);
          for (const service of services) {
            const characteristics = await withTimeout(
              service.getCharacteristics(),
              5000,
            );
            for (const c of characteristics) {
              if (c.properties.write || c.properties.writeWithoutResponse) {
                characteristic = c;
                break;
              }
            }
            if (characteristic) break;
          }
          if (!characteristic) {
            throw new Error(
              'No writable characteristic found after reconnection.',
            );
          }
        } catch (reconnectError) {
          throw new Error(
            `Bluetooth connection lost during printing (batch ${i + 1} of ${batches.length}). Reconnection failed: ${
              reconnectError instanceof Error
                ? reconnectError.message
                : 'Unknown error'
            }`,
            { cause: reconnectError },
          );
        }
      }

      await withTimeout(
        characteristic.writeValue(batch as unknown as BufferSource),
        10000,
      );

      // Add delay between batches (except after the last one)
      if (!isLastBatch && delayBetweenBatches > 0) {
        await new Promise((resolve) =>
          setTimeout(resolve, delayBetweenBatches),
        );
      }
    } catch (error) {
      // Re-throw with batch context
      throw new Error(
        `Failed to print batch ${i + 1} of ${batches.length}: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        { cause: error },
      );
    }
  }

  return device;
};

export const ESC_POS = {
  INIT: new Uint8Array([0x1b, 0x40]),
  CUT: new Uint8Array([0x1d, 0x56, 0x41, 0x03]),
  ALIGN_LEFT: new Uint8Array([0x1b, 0x61, 0x00]),
  ALIGN_CENTER: new Uint8Array([0x1b, 0x61, 0x01]),
  ALIGN_RIGHT: new Uint8Array([0x1b, 0x61, 0x02]),
  BOLD_ON: new Uint8Array([0x1b, 0x45, 0x01]),
  BOLD_OFF: new Uint8Array([0x1b, 0x45, 0x00]),
  DOUBLE_SIZE_ON: new Uint8Array([0x1d, 0x21, 0x11]),
  DOUBLE_SIZE_OFF: new Uint8Array([0x1d, 0x21, 0x00]),
};

export const printReceipt = async (
  orderData: ReceiptData,
  printer: SavedPrinter,
  activeDevice?: USBDevice | BluetoothDevice | null,
): Promise<USBDevice | BluetoothDevice | null> => {
  const encoder = new TextEncoder();
  const date = orderData.date || new Date();

  // RECEIPT
  const receiptParts: Uint8Array[] = [
    ESC_POS.INIT,
    ESC_POS.ALIGN_CENTER,
    ESC_POS.DOUBLE_SIZE_ON,
    encoder.encode("Seahra's Tapaw\n"),
    ESC_POS.DOUBLE_SIZE_OFF,
    encoder.encode('Order slip\n'),
    // Show queue number in large, center, bold font directly below "Order slip"
    orderData.queueNumber ? ESC_POS.DOUBLE_SIZE_ON : new Uint8Array(),
    orderData.queueNumber ? ESC_POS.BOLD_ON : new Uint8Array(),
    orderData.queueNumber
      ? encoder.encode(`${orderData.queueNumber}\n`)
      : new Uint8Array(),
    orderData.queueNumber ? ESC_POS.BOLD_OFF : new Uint8Array(),
    orderData.queueNumber ? ESC_POS.DOUBLE_SIZE_OFF : new Uint8Array(),
    ESC_POS.ALIGN_LEFT,
    encoder.encode('--------------------------------\n'),
    encoder.encode(`Date: ${date.toLocaleString()}\n`),
    orderData.orderNumber
      ? encoder.encode(`Order: ${orderData.orderNumber}\n`)
      : new Uint8Array(),
    orderData.customerName
      ? encoder.encode(`Customer: ${orderData.customerName}\n`)
      : new Uint8Array(),
    encoder.encode('--------------------------------\n'),
    encoder.encode('ITEM            QTY    PRICE\n'),
  ];

  orderData.items.forEach((item) => {
    const name = item.name.substring(0, 15).padEnd(16);
    const qty = item.quantity.toString().padStart(3);
    const price = (item.quantity * item.price).toFixed(2).padStart(10);
    receiptParts.push(encoder.encode(`${name}${qty}${price}\n`));
  });

  receiptParts.push(
    encoder.encode('--------------------------------\n'),
    ESC_POS.ALIGN_RIGHT,
    ESC_POS.BOLD_ON,
    encoder.encode(`TOTAL: PHP ${orderData.total.toFixed(2)}\n`),
    ESC_POS.BOLD_OFF,
    encoder.encode(`Paid via: ${orderData.paymentMethod}\n`),
    encoder.encode(`Amount Received: ${orderData.amountReceived.toFixed(2)}\n`),
    encoder.encode(`Change: ${orderData.change.toFixed(2)}\n`),
    ESC_POS.ALIGN_CENTER,
    encoder.encode('\nThank you for your purchase!\n'),
    encoder.encode('\n\n\n'),
  );

  if (orderData.shouldCut !== false) {
    receiptParts.push(ESC_POS.CUT);
  }

  // QUEUE NUMBER (Separate cut)
  if (orderData.queueNumber) {
    receiptParts.push(
      ESC_POS.INIT,
      ESC_POS.ALIGN_CENTER,
      encoder.encode('\n\n'),
      encoder.encode('QUEUE NUMBER\n'),
      ESC_POS.DOUBLE_SIZE_ON,
      ESC_POS.BOLD_ON,
      encoder.encode(`${orderData.queueNumber}\n`),
      ESC_POS.BOLD_OFF,
      ESC_POS.DOUBLE_SIZE_OFF,
      encoder.encode(`\n${date.toLocaleString()}\n`),
      ESC_POS.ALIGN_LEFT,
      encoder.encode('--------------------------------\n'),
      encoder.encode('ITEM            QTY    PRICE\n'),
    );

    // Add order items to queue slip
    orderData.items.forEach((item) => {
      const name = item.name.substring(0, 15).padEnd(16);
      const qty = item.quantity.toString().padStart(3);
      const price = (item.quantity * item.price).toFixed(2).padStart(10);
      receiptParts.push(encoder.encode(`${name}${qty}${price}\n`));
    });

    receiptParts.push(
      encoder.encode('--------------------------------\n'),
      ESC_POS.ALIGN_CENTER,
      encoder.encode('\n\n\n'),
    );

    if (orderData.shouldCut !== false) {
      receiptParts.push(ESC_POS.CUT);
    }
  }

  const finalData = concatUint8Arrays(receiptParts);
  return await printRawBatched(finalData, printer, activeDevice);
};
